import { isAxiosError } from 'axios';
import type { ContractIncomeProtectionResponse } from '~/domain/IncomeProtection/contractIncomeProtection';
import type { ContractIncomeProtectionResponseDTO } from '~/dto/IncomeProtection/contractIncomeProtection';
import { contractIncomeProtectionDTOToContractIncomeProtection } from '~/mappers/IncomeProtection/contractIncomeProtection';
import apiAssegurou from '~/services/apiAssegurou';
import { dateToISO } from '~/utils/date';
import {
  Gender,
  MaritalStatus,
  formattedGender,
  formattedMaritalStatus,
} from '~/utils/form';
import {
  getCompleteAddress,
  getPhoneInfos,
  getRawDocument,
  getRawZipcode,
  stringToCapitalCase,
} from '~/utils/string';

interface FormData extends IncomeProtectionForm.Form {
  userDocument: string | undefined;
  contractSeller: Checkout.ContractSeller;
}

export async function contractIncomeProtectionUseCase(
  data: FormData,
  payment: Checkout.Form
): Promise<ContractIncomeProtectionResponse> {
  try {
    const { creditCard } = payment;
    const [expiryDateMonth, expiryDateYear] = creditCard?.expiryDate.split(
      '/'
    ) as string[];
    const {
      introduction,
      personalInfo,
      contact,
      address,
      userDocument,
      contractSeller,
    } = data;

    const phoneInfos = getPhoneInfos(contact.cellphone);

    const date = new Date();

    const contractSellerData = contractSeller
      ? {
          lojaCod: contractSeller.store?.code,
          vendedorCod: getRawDocument(contractSeller.seller?.document ?? ''),
        }
      : null;

    const completeAddress = getCompleteAddress(address);

    const complement =
      completeAddress.length > 50
        ? '.'
        : address.complement
        ? address.complement.toUpperCase()
        : 'S/C';

    const number = completeAddress.length > 50 ? '.' : address.number ?? 'S/N';

    const response =
      await apiAssegurou.post<ContractIncomeProtectionResponseDTO>(
        'v1/Protecao/ContratarSeguroPerdaRenda',
        {
          proposta: {
            ...contractSellerData,
            vendaLoginCpf: userDocument ? getRawDocument(userDocument) : '',
            numeroApolice: '500047',
            dataVenda: date.toISOString(),
            dataEfetivacaoVenda: date.toISOString(),
            vigencia: 12,
            diaVencimentoPagamento: date.getDate(),
            valorSeguro: 17.99,
            valorPremio: 17.99,
            tipoPagto: 'CC',
            comCapitalizacao: true,
            Segurado: {
              cpfCnpjSegurado: getRawDocument(introduction.document),
              nomeSegurado: introduction.fullName.toUpperCase(),
              dataNascimento: dateToISO(personalInfo.birthDate),
              sexo: formattedGender[
                (personalInfo.gender as Gender) ?? Gender.NOT_INFORMED
              ],
              estadoCivil:
                formattedMaritalStatus[
                  (personalInfo.maritalStatus as MaritalStatus) ??
                    MaritalStatus.SINGLE
                ],
              emailSegurado: contact.email.toLowerCase(),
              dddCelular: phoneInfos.ddd,
              telefoneCelular: phoneInfos.phone,
              enderecoCep: getRawZipcode(address.zipcode),
              endereco: address.street.toUpperCase(),
              enderecoNumero: number,
              enderecoComplemento: complement,
              enderecoBairro: address.neighborhood.toUpperCase(),
              enderecoCidade: address.city.toUpperCase(),
              enderecoUf: address.state.toUpperCase(),
            },
            dadosPagamento: {
              numeroCartao: creditCard?.cardNumber.toString(),
              nomeCartao: stringToCapitalCase(creditCard?.holderName),
              mesValidadeCartao: expiryDateMonth,
              anoValidadeCartao: `20${expiryDateYear}`,
              cvv: creditCard?.CVV,
            },
            beneficiarios: [
              {
                nome: introduction.fullName.toUpperCase(),
                parentesco: 'TITULAR',
                dataNascimento: dateToISO(personalInfo.birthDate),
              },
            ],
          },
        }
      );

    return contractIncomeProtectionDTOToContractIncomeProtection(response.data);
  } catch (err) {
    if (isAxiosError(err) && err.response?.data?.message) {
      throw {
        message: err.response?.data?.message,
      };
    }
    throw {
      message: null,
    };
  }
}
